import {
  AssetGetAction,
  AssetHistoryListAction,
  AssetListAction,
  KalturaAsset,
  KalturaAssetHistoryFilter,
  KalturaAssetHistoryListResponse,
  KalturaAssetListResponse,
  KalturaChannelFilter,
  KalturaFilterPager,
  KalturaFilterPagerArgs,
  KalturaSearchAssetFilter,
  KalturaWatchStatus,
  KalturaSearchAssetFilterArgs,
  KalturaAssetReferenceType,
  FavoriteListAction,
  KalturaFavoriteFilter,
  KalturaMediaAsset,
  KalturaFavoriteListResponse,
  AssetListActionArgs,
  KalturaPersonalListSearchFilter,
  AssetHistoryListActionArgs,
  KalturaAssetMetaOrTagGroupBy,
  KalturaDetachedResponseProfile,
  KalturaAggregationCountFilter,
  KalturaOnDemandResponseProfile,
  AssetCountAction,
  KalturaLiveAsset,
  KalturaProgramAsset,
  FavoriteAddAction,
  KalturaFavorite,
  FavoriteDeleteAction,
  KalturaDynamicOrderBy,
  KalturaMetaTagOrderBy,
  KalturaAssetHistorySuppressFilter,
  ChannelGetAction,
  KalturaChannel,
  KalturaGroupingOption,
  KalturaRecordingAsset,
  KalturaScheduledRecordingProgramFilter,
  KalturaScheduledRecordingAssetType,
  KalturaAssetGroupBy,
  KalturaAssetOrderBy,
  KalturaAssetType,
  KalturaChannelFieldOrderByType,
} from '@kaltura-ott/ott-client-types/api/types';
import { AssetHistoryGetNextEpisodeAction } from '@kaltura-ott/ott-client-types/api/types/AssetHistoryGetNextEpisodeAction';
import { KalturaAssetCount } from '@kaltura-ott/ott-client-types/api/types/KalturaAssetCount';
import { KalturaAssetFilter } from '@kaltura-ott/ott-client-types/api/types/KalturaAssetFilter';
import { Observable, of, forkJoin, throwError } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';

import { KuxFavorite, KuxMediaAssetType } from '../interfaces';

import { assetToType } from './asset-v2.helper';
import { getPageNumberArr } from './asset.helper';
import { addKsql } from './category.tree.helper';
import { getConfig, KuxConfig, KuXConfig } from './config';
import { KuXDms, MediaType, ExtendedMediaType, AdditionalMediaType } from './dms';
import { kalturaClient } from './kaltura-clent';
import { CATEGORY_ID_SYSTEM_NAME } from './utils/constants';

export const ASSET_ACCEPTED_TYPES = [KalturaMediaAsset, KalturaLiveAsset, KalturaProgramAsset, KalturaRecordingAsset];
export const DEFAULT_CHUNK_SIZE = 25;
export const DEFAULT_CHUNK_SIZE_TV_CHANNELS = 500;

export const ASSET_GROUP_BY_RETRIEVED_PROPERTIES =
  'error, assetId, assetType, duration, finishedWatching, position, watchedDate, mediaFiles,description,objectType,name,id,images,tags,metas,epgChannelId,enableCatchUp,enableCdvr,enableStartOver,enableTrickPlay,linearAssetId,type,updateDate,externalId,epgId,endDate,createDate,crid,startDate';

/**
 * Get asset list by action
 *
 * @param assetListActionArgs : AssetListActionArgs
 * @param groupBy : string - Group Filter from OPC
 */
export const getAssetList = (
  assetListActionArgs: AssetListActionArgs,
  groupBy = '',
  withRetrievedProperties = false,
  responseProfile?: KalturaOnDemandResponseProfile,
): Observable<KalturaAssetListResponse> => {
  const action = new AssetListAction(assetListActionArgs);
  if (responseProfile) {
    action.setRequestOptions({ responseProfile, acceptedTypes: ASSET_ACCEPTED_TYPES });
  } else if (groupBy) {
    // ignore relatedProfiles for npvr rail
    const responseProfile = new KalturaOnDemandResponseProfile({
      ...(withRetrievedProperties ? { retrievedProperties: ASSET_GROUP_BY_RETRIEVED_PROPERTIES } : {}),
      relatedProfiles: [
        new KalturaDetachedResponseProfile({
          name: 'group_result',
          filter: new KalturaAggregationCountFilter(),
        }),
      ],
    });
    action.setRequestOptions({ responseProfile, acceptedTypes: ASSET_ACCEPTED_TYPES });
  } else {
    action.setRequestOptions({
      ...(withRetrievedProperties
        ? {
            responseProfile: new KalturaOnDemandResponseProfile({
              ...(withRetrievedProperties ? { retrievedProperties: ASSET_GROUP_BY_RETRIEVED_PROPERTIES } : {}),
            }),
          }
        : {}),
      acceptedTypes: ASSET_ACCEPTED_TYPES,
    });
  }
  const client = kalturaClient();
  const config: KuxConfig = getConfig(KuXConfig)('');
  if (config.language) client.appendDefaultRequestOptions({ language: config.language });
  return client.request(action).pipe(
    catchError((error) => {
      return throwError(error);
    }),
  );
};

/**
 * Load Assets list by provided filter.
 *
 * @param filter: KalturaChannelFilterArgs
 * @param pager: KalturaFilterPagerArgs
 */
export const getAssetsByFilter = (
  filter: KalturaSearchAssetFilterArgs,
  pager?: KalturaFilterPagerArgs,
  responseProfile?: KalturaOnDemandResponseProfile,
): Observable<KalturaAssetListResponse> => {
  const assetListActionArgs = {
    filter: new KalturaSearchAssetFilter({ ...filter }),
    pager: new KalturaFilterPager({ ...pager }),
  };
  return getAssetList(assetListActionArgs, undefined, undefined, responseProfile);
};

interface GetRecommendationAssetsArgs {
  pageIndex: number;
  pageSize: number;
  kSql: string;
  groupBy: string;
  partnerListTypeIn: string;
  orderBy: string;
}
/**
 * Get recommendation assets
 *
 * @param pageIndex
 * @param pageSize
 * @param kSql
 * @param groupBy
 * @param partnerListTypeIn
 * @param orderBy
 */
export const getRecommendationAssets = ({
  pageIndex,
  pageSize,
  kSql = '',
  groupBy = '',
  partnerListTypeIn,
  orderBy,
}: GetRecommendationAssetsArgs): Observable<KalturaAssetListResponse> => {
  const assetListActionArgs = {
    filter: new KalturaPersonalListSearchFilter({
      partnerListTypeIn,
      kSql,
      ...(groupBy ? { groupBy: [new KalturaAssetMetaOrTagGroupBy({ value: groupBy })] } : {}),
      ...(orderBy ? { orderBy: orderBy } : {}),
    }),
    pager: new KalturaFilterPager({ pageIndex, pageSize }),
  };
  return getAssetList(assetListActionArgs, groupBy);
};

/**
 * Get asset by id
 * @param id: string
 * @param assetReferenceType
 */
export const getAssetById = (
  id: number | string,
  assetReferenceType = KalturaAssetReferenceType.media,
): Observable<KalturaAsset> => {
  const action = new AssetGetAction({
    id: id.toString(),
    assetReferenceType,
  });
  action.setRequestOptions({ acceptedTypes: ASSET_ACCEPTED_TYPES });
  const client = kalturaClient();
  return client.request(action).pipe(
    catchError((error) => {
      return of(error);
    }),
  );
};

/**
 * TODO DEPRECATED
 *
 * Get assets by array of assetsId
 *
 * @param idList: number[]
 * @param kSql
 */
export const getAssetsByIdList = (idList: number[], kSql?: string): Observable<KalturaAssetListResponse> => {
  if (!idList.length) {
    return of(new KalturaAssetListResponse({}));
  }
  const filter = new KalturaSearchAssetFilter({
    kSql: addKsql(kSql, `(or ${idList.map((id) => `media_id='${id}'`).join(' ')} )`),
  });
  return getAssetList({ filter });
};

/**
 * Find assets by assetHistory.assetId
 *
 * @param history: KalturaAssetHistory[]
 * @param kSql: string
 */
export const getMediaAssetsByAssetHistory = <T extends { assetType: KalturaAssetType; assetId: number }>(
  history: T[],
  kSql?: string,
  typeIn?: string,
) => {
  const filter = new KalturaSearchAssetFilter({
    typeIn,
    kSql: addKsql(kSql, `(or ${history?.map((asset) => `${asset.assetType}_id='${asset.assetId}'`).join(' ')} )`),
  });

  return getAssetList({ filter });
};

/**
 * Get asset by category id
 *
 * @param id: number
 */
export const getAssetByCategoryId = (id: number): Observable<KalturaMediaAsset | null> => {
  const filter = new KalturaSearchAssetFilter({ kSql: `${CATEGORY_ID_SYSTEM_NAME}='${id}'` });
  return getAssetList({ filter }).pipe(
    map((resp) => (resp.objects && resp.objects.length > 0 ? (resp.objects[0] as KalturaMediaAsset) : null)),
  );
};

interface KuxAssetsByChannelArgs {
  channelId: number;
  pageIndex?: number;
  pageSize?: number;
  kSql?: string;
  groupBy?: string;
  orderBy?: string;
  orderMeta?: KalturaMetaTagOrderBy;
}

/**
 * Generate ordering object for KalturaChannelFilter
 *
 * @param {string} orderBy
 * @param {KalturaMetaTagOrderBy} orderMeta
 *
 */
function generateOrderingConfig(orderBy: string, orderMeta: KalturaMetaTagOrderBy) {
  if (!orderBy) {
    return {};
  }
  const isKalturaChannelFieldOrderByType = Object.values(KalturaChannelFieldOrderByType).includes(
    orderBy as KalturaChannelFieldOrderByType,
  );

  if (isKalturaChannelFieldOrderByType) {
    return { orderBy };
  }

  const orderByLowerCase = orderBy.toLowerCase();

  if (orderByLowerCase === 'name') {
    return { orderBy: 'NAME_ASC' };
  }

  return { dynamicOrderBy: new KalturaDynamicOrderBy({ name: orderBy, orderBy: orderMeta }) };
}

/**
 * Get Asset List
 * @param channelId
 * @param pageIndex
 * @param pageSize
 * @param kSql
 * @param groupBy
 * @param orderBy
 * @param orderMeta
 */
export const getAssetsByChannelId = ({
  channelId,
  pageIndex = 1,
  pageSize = DEFAULT_CHUNK_SIZE,
  kSql,
  groupBy = '',
  orderBy = '',
  orderMeta = KalturaMetaTagOrderBy.metaAsc,
}: KuxAssetsByChannelArgs): Observable<KalturaAssetListResponse> => {
  let filter;
  if (channelId) {
    filter = new KalturaChannelFilter({
      idEqual: channelId,
      kSql,
      ...(groupBy ? { groupBy: [new KalturaAssetMetaOrTagGroupBy({ value: groupBy })] } : {}),
      ...generateOrderingConfig(orderBy, orderMeta),
    });
  } else {
    filter = new KalturaSearchAssetFilter({
      ...(groupBy ? { groupBy: [new KalturaAssetMetaOrTagGroupBy({ value: groupBy })] } : {}),
      kSql,
    });
  }

  const pager = new KalturaFilterPager({
    pageIndex,
    pageSize,
  });

  return getAssetList({ filter, pager }, groupBy);
};

interface GetAssetsRecursivelyArgs {
  filter: KalturaAssetFilter;
  chunkSize?: number;
}

export interface KuxAssetListResponse {
  totalCount: number;
  objects: KalturaAsset[];
}
/**
 * Load all assets by chunks.
 *
 * @param filter
 * @param chunkSize
 * @param groupBy
 */
export const getAssetsRecursively = (
  { filter, chunkSize = DEFAULT_CHUNK_SIZE }: GetAssetsRecursivelyArgs,
  groupBy = '',
  withRetrievedProperties = true,
  responseProfile?: KalturaOnDemandResponseProfile,
): Observable<KalturaAssetListResponse> => {
  const action = new AssetListAction({
    filter,
    pager: new KalturaFilterPager({
      pageIndex: 1,
      pageSize: chunkSize,
    }),
  });
  if (responseProfile) {
    action.setRequestOptions({ responseProfile, acceptedTypes: ASSET_ACCEPTED_TYPES });
  } else if (groupBy) {
    const responseProfile = new KalturaOnDemandResponseProfile({
      ...(withRetrievedProperties ? { retrievedProperties: ASSET_GROUP_BY_RETRIEVED_PROPERTIES } : {}),
      relatedProfiles: [
        new KalturaDetachedResponseProfile({
          name: 'group_result',
          filter: new KalturaAggregationCountFilter(),
        }),
      ],
    });

    action.setRequestOptions({ responseProfile, acceptedTypes: ASSET_ACCEPTED_TYPES });
  } else {
    action.setRequestOptions({ acceptedTypes: ASSET_ACCEPTED_TYPES });
  }
  const client = kalturaClient();
  return client.request(action).pipe(
    switchMap((assets) => {
      const paginatorRequestArr = getPageNumberArr(assets.totalCount, chunkSize, 2).map((pageNumber) =>
        getAssetList(
          {
            filter,
            pager: new KalturaFilterPager({
              pageIndex: pageNumber,
              pageSize: chunkSize,
            }),
          },
          undefined,
          undefined,
          responseProfile,
        ),
      );
      return forkJoin([of(assets), ...paginatorRequestArr]);
    }),
    map((result) => {
      return {
        ...result[0],
        totalCount: result[0].totalCount,
        objects: result.reduce((prev: KalturaAsset[], cur) => [...prev, ...cur.objects], []),
      } as KalturaAssetListResponse;
    }),
  );
};

export const getAssetHistory = (
  assetHistoryListActionArgs: AssetHistoryListActionArgs,
  groupBy = '',
  suppress = false,
) => {
  const action = new AssetHistoryListAction(assetHistoryListActionArgs);

  // we can't have 2 responseProfiles, if we have groupBy and suppress enabled we use suppress
  if (suppress) {
    const responseProfile = new KalturaOnDemandResponseProfile({
      relatedProfiles: [
        new KalturaDetachedResponseProfile({
          name: 'suppress',
          filter: new KalturaAssetHistorySuppressFilter(),
        }),
      ],
    });
    action.setRequestOptions({ responseProfile });
  } else if (groupBy) {
    const responseProfile = new KalturaOnDemandResponseProfile({
      retrievedProperties: ASSET_GROUP_BY_RETRIEVED_PROPERTIES,
      relatedProfiles: [
        new KalturaDetachedResponseProfile({
          name: 'group_result',
          filter: new KalturaAggregationCountFilter(),
        }),
      ],
    });
    action.setRequestOptions({ responseProfile });
  }

  const client = kalturaClient();
  return client.request(action).pipe(
    catchError((error) => {
      return of(error);
    }),
  );
};

interface GetAssetHistoryListRecursivelyArgs {
  filter: KalturaAssetHistoryFilter;
  chunkSize?: number;
}

export const getAssetHistoryListRecursively = <T extends KalturaAssetHistoryListResponse>(
  { filter, chunkSize = DEFAULT_CHUNK_SIZE }: GetAssetHistoryListRecursivelyArgs,
  groupBy = '',
  suppress = false,
): Observable<T> => {
  const action = new AssetHistoryListAction({
    filter,
    pager: new KalturaFilterPager({
      pageIndex: 1,
      pageSize: chunkSize,
    }),
  });
  const client = kalturaClient();

  // we can't have 2 responseProfiles, if we have groupBy and suppress enabled we use suppress
  if (suppress) {
    const responseProfile = new KalturaOnDemandResponseProfile({
      relatedProfiles: [
        new KalturaDetachedResponseProfile({
          name: 'suppress',
          filter: new KalturaAssetHistorySuppressFilter(),
        }),
      ],
    });
    action.setRequestOptions({ responseProfile });
  } else if (groupBy) {
    const responseProfile = new KalturaOnDemandResponseProfile({
      retrievedProperties: ASSET_GROUP_BY_RETRIEVED_PROPERTIES,
      relatedProfiles: [
        new KalturaDetachedResponseProfile({
          name: 'group_result',
          filter: new KalturaAggregationCountFilter(),
        }),
      ],
    });
    action.setRequestOptions({ responseProfile });
  }

  return client.request(action).pipe(
    switchMap((assets) => {
      const paginatorRequestArr = getPageNumberArr(assets.totalCount, chunkSize, 2).map((pageNumber) =>
        getAssetHistory({
          filter,
          pager: new KalturaFilterPager({
            pageIndex: pageNumber,
            pageSize: chunkSize,
          }),
        }),
      );
      return forkJoin([of(assets), ...paginatorRequestArr]);
    }),
    map((result) => {
      return {
        ...result[0],
        totalCount: result[0].totalCount,
        objects: result.reduce((prev, cur) => [...prev, ...cur.objects], []),
      };
    }),
  ) as Observable<T>;
};

export interface KuxAssetHistoryListArgs {
  pageIndex?: number;
  pageSize?: number;
  orderBy?: string;
  kSql?: string;
  groupBy?: string;
  daysLessThanOrEqual?: number;
  statusEqual?: KalturaWatchStatus;
  typeIn?: string;
  suppress?: boolean;
  assetIdIn?: string;
}

/**
 * Get list of AssetHistory
 * @param channelId
 * @param pageIndex
 * @param pageSize
 */
export const getAssetHistoryList = ({
  typeIn,
  pageSize,
  pageIndex,
  assetIdIn,
  statusEqual = KalturaWatchStatus.all,
  daysLessThanOrEqual = 50,
  groupBy = '',
  suppress = false,
  kSql = '',
}: KuxAssetHistoryListArgs) => {
  const filter = new KalturaAssetHistoryFilter({
    typeIn,
    assetIdIn,
    statusEqual,
    daysLessThanOrEqual,
    kSql,
  });

  if (pageSize) {
    const pager = new KalturaFilterPager({
      pageSize,
      pageIndex,
    });

    return getAssetHistory({ filter, pager }, groupBy, suppress).pipe(
      catchError((error) => {
        return of(error);
      }),
    );
  }

  return getAssetHistoryListRecursively({ filter }, groupBy, suppress).pipe(
    catchError((error) => {
      return of(error);
    }),
  );
};

/**
 * AssetFavoriteList Arguments
 */
interface KuxAssetFavoriteListArgs {
  mediaType: MediaType;
}

/**
 * Get all favorite assetId by media type
 * (function has cache)
 *
 * @param mediaType: KuxAssetFavoriteListArgs
 */
export const getAssetFavoriteList = ({ mediaType }: KuxAssetFavoriteListArgs): Observable<KuxFavorite[]> => {
  const action = new FavoriteListAction({
    filter: new KalturaFavoriteFilter({
      mediaTypeEqual: Number(KuXDms.mediaTypes[mediaType]),
    }),
  });
  const client = kalturaClient();
  return client.request(action).pipe(
    map((favoriteListResponse: KalturaFavoriteListResponse) => {
      const favoriteValue = favoriteListResponse.objects.map((object) => ({
        assetId: object.assetId,
        createDate: object.createDate,
        extraData: object.extraData,
      }));
      return favoriteValue;
    }),
    catchError(() => {
      return of([]);
    }),
  );
};

/**
 * Adds asset to favorites
 * (updates cache)
 *
 * @param id: KuxAssetFavoriteListArgs
 */
export const addFavorite = (id: number): Observable<KalturaFavorite> => {
  const action = new FavoriteAddAction({
    favorite: new KalturaFavorite({ assetId: id }),
  });
  const client = kalturaClient();
  return client.request(action);
};

/**
 * Removes asset from favorites
 * (updates cache)
 *
 * @param id: KuxAssetFavoriteListArgs
 */
export const removeFavorite = (id: number): Observable<boolean> => {
  const action = new FavoriteDeleteAction({ id });
  const client = kalturaClient();
  return client.request(action);
};

/**
 * Get asset list by EPG ids
 */
export const getAssetListByEpgIds = (ids: number[], ksql: string, groupBy?: string) => {
  const kSql = addKsql(ksql, `(and epg_id: ${ids.join(',')})`);
  const filter = new KalturaSearchAssetFilter({
    kSql,
    ...(groupBy ? { groupBy: [new KalturaAssetMetaOrTagGroupBy({ value: groupBy })] } : {}),
  });
  return getAssetsRecursively({ filter }, groupBy);
};

/**
 * Get asset list by asset type
 */
export const getAssetListByAssetType = (
  assetType: ExtendedMediaType,
  ksql?: string,
  groupBy?: string,
  pageIndex?: number,
  pageSize?: number,
  orderBy?: string,
) => {
  const originKsql = `(and asset_type='${assetType}')`;
  const kSql = addKsql(ksql, originKsql);
  const filter = new KalturaSearchAssetFilter({
    kSql,
    ...(groupBy ? { groupBy: [new KalturaAssetMetaOrTagGroupBy({ value: groupBy })] } : {}),
    ...(orderBy ? { orderBy } : {}),
  });
  if (!pageIndex && !pageSize) {
    return getAssetsRecursively({ filter }, groupBy);
  } else {
    return getAssetList({ filter, pager: new KalturaFilterPager({ pageSize, pageIndex }) }, groupBy);
  }
};

export const getAssetRecordingList = (
  ksql?: string,
  groupBy?: string,
  pageIndex?: number,
  pageSize?: number,
  orderBy?: string,
  groupingOptionEqual?: KalturaGroupingOption,
): Observable<KuxAssetListResponse> => {
  const originKsql = `(and asset_type='${AdditionalMediaType.Recording}')`;
  const kSql = addKsql(ksql, originKsql);

  const filter = new KalturaSearchAssetFilter({
    kSql,
    ...(groupingOptionEqual ? { groupingOptionEqual } : {}),
    ...(groupBy ? { groupBy: [new KalturaAssetMetaOrTagGroupBy({ value: groupBy })] } : {}),
    ...(orderBy ? { orderBy } : {}),
  });
  if (!pageIndex && !pageSize) {
    return getAssetsRecursively({ filter }, groupBy, false);
  } else {
    return getAssetList({ filter, pager: new KalturaFilterPager({ pageSize, pageIndex }) }, groupBy, false);
  }
};

export const getScheduledRecordingAssetsList = (
  pageIndex?: number,
  pageSize?: number,
  seriesId?: string,
  groupBy?: string,
  orderBy = KalturaAssetOrderBy.startDateAsc,
) => {
  let filter = new KalturaScheduledRecordingProgramFilter({
    recordingTypeEqual: KalturaScheduledRecordingAssetType.single,
    orderBy,
  });

  if (seriesId) {
    filter = new KalturaScheduledRecordingProgramFilter({
      recordingTypeEqual: KalturaScheduledRecordingAssetType.series,
      seriesIdsIn: seriesId,
      orderBy,
    });
  }
  if (!pageIndex && !pageSize) {
    return getAssetsRecursively({ filter }, groupBy, false);
  } else {
    return getAssetList({ filter, pager: new KalturaFilterPager({ pageSize, pageIndex }) }, groupBy, false);
  }
};

export const getScheduledRecordingProgram = () => {
  const filter = new KalturaScheduledRecordingProgramFilter({
    recordingTypeEqual: KalturaScheduledRecordingAssetType.single,
  });

  return getAssetsRecursively({ filter }, undefined, true);
};

export const getAssetsByCategoryIds = (ids: number[], pageSize = 50): Observable<KuxAssetListResponse | undefined> => {
  const filter = new KalturaSearchAssetFilter({
    kSql: `(and (or ${ids.map((id) => `${CATEGORY_ID_SYSTEM_NAME} = '${id}' `).join('')})(or ))`,
  });
  return getAssetsRecursively({ filter, chunkSize: pageSize }).pipe(
    map((resp) => (resp.objects && resp.objects.length > 0 ? resp : undefined)),
  );
};

interface GetAssetCountArgs {
  kSql: string;
  groupBy: KalturaAssetGroupBy[];
  orderBy?: string;
}

export const getAssetCount = ({
  kSql,
  groupBy,
  orderBy = 'NAME_ASC',
}: GetAssetCountArgs): Observable<KalturaAssetCount> => {
  const filter = new KalturaSearchAssetFilter({
    kSql,
    groupBy,
    orderBy,
  });

  const action = new AssetCountAction({ filter });
  const client = kalturaClient();

  return client.request(action).pipe(
    catchError((error) => {
      return of(error);
    }),
  );
};

export const getCollectionById = (id: number): Observable<KalturaChannel> => {
  const action = new ChannelGetAction({ id });
  const client = kalturaClient();
  return client.request(action).pipe(
    catchError((error) => {
      return of(error);
    }),
  );
};

//Deprecated see getSeriesAsset
export const getSeriesAssetBySeriesId = async (seriesId: string) => {
  //TODO: remove toPromise and do refactoring this function
  const kSql = `(and SeriesID = '${seriesId}' asset_type = '${KuXDms.mediaTypes[MediaType.Series]}')`;
  const seriesAssetResult = await getAssetsByFilter({ kSql }).toPromise();
  return seriesAssetResult?.objects?.length ? seriesAssetResult?.objects[0] : undefined;
};

export const getAssetHistoryNextEpisode = async (seriesId: string) => {
  //TODO: remove toPromise and do refactoring this function
  const seriesAsset = await getSeriesAssetBySeriesId(seriesId);
  if (!seriesAsset?.id) return 0;
  const action = new AssetHistoryGetNextEpisodeAction({
    assetId: seriesAsset.id,
  });
  const client = kalturaClient();
  return client
    .request(action)
    .pipe(
      map((assetHistory) => {
        return assetHistory?.assetId;
      }),
      catchError((error) => {
        return of(0);
      }),
    )
    .toPromise();
};

export const getAssetsByMediaType = <T extends KuxMediaAssetType>(
  assetType: string,
  pageSize = DEFAULT_CHUNK_SIZE,
): Observable<T[]> => {
  return getAssetsByFilter(
    { kSql: `(and asset_type='${assetType}')` },
    {
      pageSize,
      pageIndex: 1,
    },
  ).pipe(
    map((asset) => {
      return asset.objects.map((item) => assetToType<KalturaAsset, T>(item)).filter(Boolean) as T[];
    }),
  );
};

export const getAssetsIdBySearchFilter = (kSql: string, pageSize = DEFAULT_CHUNK_SIZE): Observable<number[]> => {
  const responseProfile = new KalturaOnDemandResponseProfile({
    retrievedProperties: 'id, error',
    relatedProfiles: [
      new KalturaDetachedResponseProfile({
        name: 'group_result',
        filter: new KalturaAggregationCountFilter(),
      }),
    ],
  });

  return getAssetsByFilter(
    { kSql },
    {
      pageSize,
      pageIndex: 1,
    },
    responseProfile,
  ).pipe(
    map((response) => {
      return response.objects.map((item) => item.id);
    }),
  );
};
